<template>
    <div
        class="eq-viewcontent"
        :class="{ 'eq-fill-content': fillContent }"
        ref="container"
        :style="[
            props.height !== undefined ? `height: ${props.height}vh;` : '',
            props.minHeight !== undefined ? `min-height: ${props.minHeight}vh;` : '',
        ]"
    >
        <LoadingComp
            v-if="!quietLoad"
            ref="loadingComp"
            :label="loaderLabel"
            :state="loadState"
            :show-as-modal="showAsModal"
            :confirm-close="confirmModalClose"
            @closed="emit('loadingModalClosed')"
            class="mt-4"
        />

        <ErrorMessage
            v-if="errormessage"
            :message="errormessage"
        />
        <UnsavedChangesMessage v-if="hasUnsavedChanges" />
        <SavedChangesFadeout v-if="isSaved" />
        <SaveFailedMessage v-if="isFailed" />

        <slot
            name="default"
            v-if="showAsModal || !loadState || (!loadState?.isActive && loadState.Failed.length === 0)"
        ></slot>

        <ContentFooter v-if="$slots.footer || savefunction !== undefined">
            <slot
                name="footer"
                v-if="$slots.footer"
            ></slot>
            <ButtonComp
                v-if="savefunction != undefined && !$slots.footer"
                button-type="Save"
                :loadstate="saveState"
                @click="onSave"
                :disabled="!hasUnsavedChanges"
                >{{ $tm('button.save') }}
            </ButtonComp>
        </ContentFooter>
    </div>
</template>

<script lang="ts" setup>
import {
    ButtonComp,
    ContentFooter,
    LoadingComp,
    SavedChangesMessage,
    SaveFailedMessage,
    UnsavedChangesMessage,
    ErrorMessage,
} from '@/components';
import { useTaskLoader, type TaskLoaderState } from '@/composeables/loaders';
import type { ITranslationKey } from '@/objects/TranslationKey';
import { useToastStore } from '@/stores';
import { useElementVisibility } from '@vueuse/core';
import { watch, nextTick, ref, onUnmounted } from 'vue';
import SavedChangesFadeout from '../Alert/SavedChangesFadeout.vue';

const loadingComp = ref<InstanceType<typeof LoadingComp> | null>(null);

const container = ref<HTMLElement | null>(null);

// We define the height types that we support for the content
export type HeightTypes = 10 | 25 | 33 | 50 | 66 | 75 | 100 | undefined;

const props = withDefaults(
    defineProps<{
        /**
         * The state of the loader
         */
        loadState?: TaskLoaderState;

        /**
         * Trasnslation key for the label to show in the loader
         */
        loaderLabel?: string;

        /**
         * If the loader should be shown as a modal
         * @default false
         */
        showAsModal?: boolean;

        /**
         * If the user should confirm before closing the loader
         * @default false
         */
        confirmModalClose?: boolean;

        /**
         * If there are unsaved changes in the view
         * @default false
         */
        hasUnsavedChanges?: boolean;

        /**
         * If there are saved changes in the view
         * @default false
         */
        hasSavedChanges?: boolean;

        /**
         * The error message to show
         */
        errormessage?: ITranslationKey;

        /**
         * The height of the content
         * @default undefined
         */
        height?: HeightTypes;

        /**
         * The minimum height of the content
         * @default undefined
         */
        minHeight?: HeightTypes;

        /**
         * If the content should fill the content. Removes the margin and padding
         * @default false
         */
        fillContent?: boolean;

        savefunction?: () => Promise<boolean>;

        /**
         * We will try and autoresize the content to fit available space of the Layout content view wrapper.
         * This should only be done when we want an "infinitate" List at the bottom of the page but only scroll the list content and not the entire page.
         * TODO: This one should autoadjust the height of the content whenever we modify the DOM of the content so we never end up with an additional scrollbar.
         * @default false
         */
        autoresize?: boolean;

        /**
         * If the content should be loaded quietly without any loading indication
         * This is useful when the content is loaded in the background and the user should not be disturbed
         * @remarks this only works with show-as-modal = true;
         * @default false
         */
        quietLoad?: boolean;
    }>(),
    {
        showAsModal: false,
        confirmModalClose: false,
        hasUnsavedChanges: false,
        hasSavedChanges: false,
        fillContent: false,
        height: undefined,
        autoresize: false,
        quietLoad: false,
    },
);

const emit = defineEmits<{
    (e: 'isLoaded', value: boolean): void;
    (e: 'isVisible', value: boolean): void;
    (e: 'loadingModalClosed'): void;
}>();

const isSaved = ref(props.hasSavedChanges);
const isFailed = ref(false);

const isVisible = useElementVisibility(container);

watch(
    () => props.loadState?.isActive,
    () => {
        nextTick(() => {
            emit('isLoaded', !props.loadState?.isActive);
        });
    },
    { immediate: true },
);

const { AddLoader, LoadState: saveState } = useTaskLoader();

// Helper
const closeModal = ref(false);

/**
 * We want to destroy the component and create it again.
 */
const onClosedModal = () => {
    closeModal.value = true;
    setTimeout(() => {
        closeModal.value = false;
    }, 1000);
};

/**
 * This is the default save button that is in the footer of the content if you provide a save function
 */
const onSave = async () => {
    if (props.savefunction ?? null != null) {
        isSaved.value = false;
        AddLoader(async () => {
            try {
                let result = await props.savefunction!();

                if (result) {
                    isSaved.value = true;
                    isFailed.value = false;
                }
            } catch (e: any) {
                isFailed.value = true;
                isSaved.value = false;
                useToastStore().addError(e.message);
                throw e;
            }
        }, 'general.save');
    }
};

// Emit the visibility of the content
watch(isVisible, (value) => emit('isVisible', value), { immediate: true });

watch(
    () => props.hasSavedChanges,
    (value) => {
        isSaved.value = value;
    },
);

/**
 * When we destroy the component we emit that it is not visible anymore
 */
onUnmounted(() => {
    emit('isVisible', false);
});

defineExpose({
    loadingComp,
});
</script>
